<template>
  <div v-if="Object.keys(map).length">
    <div class="map-sidebar-overlay d-flex flex-row">
      <div class="map-sidebar flex-grow-1">
        <div class="px-4 mt-2">
          <div class="text-h6">Image Selection Settings</div>
        </div>
        <!--
        height calculation below allows us to keep the title
        and reset/create footer buttons sticky while providing
        a scroll bar if the panels expanded are greater than
        the available height

        64px = app header height
        48px = title height + 8px top margin
        68px = clear/select footer height
        20px = padding for top and bottom
      -->
        <div
          style="
            max-height: calc(100vh - (64px + 48px + 68px + 20px));
            overflow: auto;
          "
          class="px-4"
        >
          <v-expansion-panels v-model="panel" multiple accordion tile focusable>
            <aoi-panel
              ref="aoiPanel"
              :map="map"
              :stack_depth="this.stack_depth"
              @mounted="onAoiMounted"
              highlight
            />
            <v-expansion-panel
              id="filter-panel"
              v-show="filters_auto_opened_once"
            >
              <v-expansion-panel-header>
                <div class="d-flex align-center">
                  <div
                    :class="{
                      'error--text': !areParamsValid,
                    }"
                  >
                    Parameters
                  </div>
                </div>
              </v-expansion-panel-header>
              <!-- eager attribute makes sure to render the query-params component so we have a ref to it on mount -->
              <v-expansion-panel-content eager>
                <query-params
                  ref="queryParams"
                  :select="this.$route.params.request_details"
                  @change="onQueryParamsChange"
                  @mounted="onQueryParamsMounted"
                />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
        <div v-if="map" class="pa-4 d-flex justify-space-between">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="clear-button"
                v-if="mounted"
                :disabled="
                  ((Array.isArray($refs.aoiPanel.aois) &&
                    $refs.aoiPanel.aois.length > 0) ||
                  areParamsDirty
                    ? false
                    : true) || pending
                "
                v-bind="attrs"
                v-on="on"
                @click="onClear"
                outlined
                >Clear</v-btn
              >
            </template>
            Remove AOI's and reset filters
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="select-button"
                v-if="mounted"
                :disabled="isInvalidForm"
                v-bind="attrs"
                v-on="on"
                @click.prevent="submit"
                color="primary"
                outlined
              >
                <spinner v-if="pending" small> Selecting... </spinner>
                <span v-else> View Selection </span>
              </v-btn>
            </template>
            Create selection based on the AOI's and filters chosen
          </v-tooltip>
        </div>
      </div>
    </div>
    <div class="map-search-container px-4">
      <div id="geocoder-container"></div>
    </div>
    <div
      v-show="$refs.aoiPanel && $refs.aoiPanel.processingAreas"
      class="map-loading-overlay"
    >
      <div class="text-center pa-2 white--text">
        <spinner>Loading areas...</spinner>
      </div>
    </div>
    <div v-show="pending" class="map-padded-overlay">
      <div class="text-center pa-2">
        <spinner>{{ this.loading_message }}...</spinner>
      </div>
    </div>
    <confirm-modal
      ref="confirmResetModal"
      title="Clear?"
      confirm-btn-text="Yes, Clear"
      cancel-btn-text="No, Cancel"
      cancel-btn-class="v-btn--flat v-btn--outlined outlined--text"
      confirm-btn-class="black--text ml-4"
    >
      <div style="max-width: 350px">
        Clearing will remove all AOI's and restore the filters default values.
        Are you sure you want to continue?
      </div>
    </confirm-modal>
  </div>
</template>

<script>
import AoiPanel from "@/components/aoiPanel/AOIPanel";
import QueryParams from "@/components/select/QueryParams";
import Spinner from "@/components/shared/Spinner";
import ConfirmModal from "@/components/shared/ConfirmModal";
import axios from "axios";
import mapUtils from "@/utils/map";
import _ from "lodash";

import { createNamespacedHelpers } from "vuex-composition-helpers";
const uiStore = createNamespacedHelpers("ui");

export default {
  props: {
    map: {
      type: Object,
      required: true,
    },
    drawnFeature: {
      type: Object,
      default: null,
    },
    map_loaded: {
      type: Boolean,
      required: true,
    },
  },
  mixins: [mapUtils],
  components: {
    QueryParams,
    Spinner,
    ConfirmModal,
    AoiPanel,
  },
  data() {
    return {
      panel: [],
      areParamsValid: true, // by default they are valid
      areParamsDirty: false,
      pending: false,
      aoiPanelMounted: false,
      mounted: false,
      response: null,
      stack_depth: 5,
      loading_message: "Creating Selection",
      filters_auto_opened_once: false,
      aoi_panel_auto_opened_once: false,
    };
  },
  computed: {
    isInvalidForm() {
      // when creating a select the form is INVALID if we don't have at least one valid AOI or valid params or are already creating a select
      return (
        !this.$refs.aoiPanel.aoisValid || !this.areParamsValid || this.pending
      );
    },
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  mounted() {
    this.mounted = true;
    this.initMap();
  },
  beforeDestroy() {
    this.map.setDrawMode("simple_select");
    this.aoiPanelMounted = false;
    this.mounted = false;
  },
  watch: {
    drawnFeature: {
      immediate: false,
      deep: true,
      handler(newValue, oldValue) {
        /*
        console.log(
          `drawnFeature newValue::${
            newValue ? JSON.stringify(newValue) : "null"
          }`
        );*/

        if (newValue && newValue !== oldValue) {
          this.initDrawnFeature(newValue);
        }
      },
    },
  },
  methods: {
    initMap() {
      if (this.map_loaded) {
        this.setActivePanel();
      } else {
        setTimeout(this.initMap, 200);
      }
    },
    onAoiMounted() {
      // console.log(`onAoiMounted`);
      this.aoiPanelMounted = true;

      // let the draw controls load before we set the draw mode
      this.$nextTick(() => {
        if (this.drawnFeature) {
          this.initDrawnFeature(this.drawnFeature);
        } else {
          this.setActivePanel();
        }

        // need to delay setting draw mode to let everything initialize correctly
        setTimeout(
          (() => {
            if (this.drawnFeature) {
              this.map.setDrawMode("simple_select");
            } else {
              // default to draw_rectangle if we don't have a drawn feature from build selection or edit selection
              this.map.setDrawMode("draw_rectangle");
            }
          }).bind(this),
          400
        );
      });
    },
    initDrawnFeature(drawnFeature) {
      if (drawnFeature) {
        const feature =
          drawnFeature.type.toLowerCase() !== "feature"
            ? { type: "Feature", geometry: drawnFeature }
            : drawnFeature;

        this.$refs.aoiPanel.aois.push(drawnFeature);
        this.setActivePanel();
      }
    },
    onQueryParamsMounted() {
      const model = this.$refs.queryParams.model;
      this.stack_depth = model.stackDepthLimit
        ? parseInt(model.stackDepth)
        : null;
    },
    setActivePanel() {
      // console.log(`setActivePanel`);
      if (this.$refs.aoiPanel.aoisValid && !this.filters_auto_opened_once) {
        this.$log.debug("Opening parameters panel");
        this.panel = [1];
        this.filters_auto_opened_once = true;
      } else if (this.map_loaded && !this.aoi_panel_auto_opened_once) {
        this.$log.debug("Opening aoi panel");
        this.panel = [0];
        this.aoi_panel_auto_opened_once = true;
      }
    },
    setFeatureCollection(featureCollection) {
      /*
      console.log(
        `setFeatureCollection(featureCollection=${JSON.stringify(
          featureCollection
        )}`
      );
      */

      // workaround for this issue: https://github.com/mapbox/mapbox-gl-draw/issues/774
      // just in case we get an invalid geojson geometry within a feature sent to us after draw
      if (
        Array.isArray(featureCollection.features) &&
        featureCollection.features.length > 0
      ) {
        const cleanAois = this.getCleanAois(featureCollection.features);
        this.$refs.aoiPanel.aois = [...cleanAois];
        this.setActivePanel();
      } else {
        this.$refs.aoiPanel.aois = [];
      }
    },
    onClear() {
      this.$refs.confirmResetModal.show().then((yes) => {
        if (yes) {
          // delete all the aois
          this.map.draw.deleteAll();
          this.$refs.aoiPanel.aois = [];
          this.$refs.queryParams.reset();
        }
      });
    },
    async submit() {
      this.createSelection();
    },
    async createSelection() {
      try {
        this.pending = true;
        this.select_loading_messages();
        const intersects = this.getIntersects(true);

        if (!intersects) {
          console.error(
            "Cannot create selection since all the AOI's have invalid coordinates."
          );
          return;
        }

        const model = this.$refs.queryParams.model;
        let params = {
          intersects: intersects.geometry || intersects,
        };

        if (model.stackDepthLimit) {
          params.stack_depth = Number(model.stackDepth);
        }

        if (model.dateOption === "image_age_category") {
          if (model.imageCategories.length > 0) {
            params.image_age_category = model.imageCategories.map((item) => {
              return item.value || item;
            });
          }
        } else if (model.dateOption === "datetime") {
          if (model.startDate && model.endDate) {
            params.datetime = `${model.startDate}/${model.endDate}`;
          } else if (model.startDate) {
            params.datetime = `${model.startDate}/..`;
          } else if (model.endDate) {
            params.datetime = `../${model.endDate}`;
          }

          if (
            model.datetime.image_age_categories.enabled &&
            model.imageCategories.length > 0
          ) {
            params.image_age_category = model.imageCategories.map((item) => {
              return item.value || item;
            });
          }
        } else if (
          model.dateOption === "seasonal" &&
          model.seasonal.years_between.enabled
        ) {
          if (
            model.seasonal.years_between.start &&
            model.seasonal.years_between.end
          ) {
            params.datetime = `${model.seasonal.years_between.start}/${model.seasonal.years_between.end}`;
          } else if (model.seasonal.years_between.start) {
            params.datetime = `${model.seasonal.years_between.start}/..`;
          } else if (model.seasonal.years_between.end) {
            params.datetime = `../${model.seasonal.years_between.end}`;
          }
        }

        if (
          model.maxCloudCover ||
          model.minDataPercent ||
          model.maxOffNadir ||
          model.minSunElevation ||
          model.sensors.length ||
          model.p3dr_support ||
          model.hd_support ||
          (model.dateOption === "seasonal" &&
            model.seasonal.type === "collect_month" &&
            model.seasonal.months.length > 0) ||
          this.isValidCollectMonthDayBetween(model) ||
          this.isValidCollectMonthDayOr(model) ||
          this.isValidCollectDayBetween(model) ||
          model.name
        ) {
          params.query = {};

          if (model.p3dr_support) {
            params.query.p3dr_support = {
              eq: model.p3dr_support,
            };
          }

          if (model.hd_support) {
            params.query["pan_resolution_avg"] = {
              gte: 0.4,
            };
          }

          if (model.maxCloudCover) {
            params.query["tile:cloud_percentage"] = {
              lte: Number(model.maxCloudCover),
            };
          }

          if (model.minDataPercent) {
            params.query["tile:data_percentage"] = {
              gte: Number(model.minDataPercent),
            };
          }

          if (model.maxOffNadir) {
            params.query["view:off_nadir"] = {
              lte: Number(model.maxOffNadir),
            };
          }

          if (model.minSunElevation) {
            params.query["view:sun_elevation"] = {
              gte: Number(model.minSunElevation),
            };
          }

          if (model.sensors.length > 0) {
            params.query.platform = {
              in: model.sensors.map((item) => {
                return item.value || item;
              }),
            };
          }

          if (
            model.dateOption === "seasonal" &&
            model.seasonal.type === "collect_month" &&
            model.seasonal.months.length > 0
          ) {
            params.query.collect_month = {
              in: model.seasonal.months.map((item) => {
                return item.value || item;
              }),
            };
          }

          if (this.isValidCollectMonthDayBetween(model)) {
            params.query.collect_month_day = {
              between: [
                `${model.seasonal.between.start.month}/${model.seasonal.between.start.date}`,
                `${model.seasonal.between.end.month}/${model.seasonal.between.end.date}`,
              ],
            };
          }

          if (this.isValidCollectMonthDayOr(model)) {
            params.query.collect_month_day = {
              or: [
                {
                  lt: `${model.seasonal.or.lt.month}-${model.seasonal.or.lt.date}`,
                },
                {
                  gt: `${model.seasonal.or.gt.month}-${model.seasonal.or.gt.date}`,
                },
              ],
            };
          }

          if (this.isValidCollectDayBetween(model)) {
            const start = parseInt(model.seasonal.days_between.start);
            const end = parseInt(model.seasonal.days_between.end);
            params.query.collect_day = {
              between: [
                start < 10 ? `0${start}` : Number(start).toLocaleString(),
                end < 10 ? `0${end}` : Number(end).toLocaleString(),
              ],
            };
          }

          if (model.name) {
            params.query_name = model.name;
          }
        }

        const url = `https://${process.env.VUE_APP_ARD_DOMAIN}/metadata/cells/acquisitions?format=geojson`;
        const response = await axios.post(url, params);

        if (response && response.data && response.data.query_id) {
          this.SHOW_SNACKBAR({
            text: `Selection ${response.data.query_id} successfully created.`,
            color: "success",
          });

          this.$emit("select_response", {
            request: params,
            response: response.data,
          });

          // route to the view selection page
          this.$router.push({
            query: {
              view: response.data.query_id,
            },
          });
        } else {
          let errorMsg = `Unexpected response recieved - ${JSON.stringify(
            response
          )}`;

          if (response.data && response.data.error_message) {
            if (
              response.data.error_message.Error &&
              response.data.error_message.Cause
            ) {
              errorMsg = `${response.data.error_message.Error} - ${response.data.error_message.Cause}`;
            } else {
              errorMsg = JSON.stringify(response.data.error_message);
            }
          }

          throw new Error(errorMsg);
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.pending = false;
      }
    },
    onQueryParamsChange(model, { valid, dirty }) {
      /*
      console.log(
        `onQueryParamsChange(model=${JSON.stringify(
          model
        )}, valid=${valid}, dirty=${dirty})`
      );*/
      this.stack_depth = model.stackDepthLimit
        ? parseInt(model.stackDepth)
        : null;
      this.areParamsValid = valid;
      this.areParamsDirty = dirty;
    },

    select_loading_messages() {
      let delay = 1000;
      for (let count = 0; count < 10; count++) {
        setTimeout(() => {
          if (
            this.loading_message.startsWith("Creating Selection") ||
            this.loading_message.startsWith("Gathering matching images")
          ) {
            this.loading_message = `Gathering matching images (${count}/10)`;
          }
        }, delay);
        delay += 2000;
      }
    },

    isValidCollectMonthDayBetween(model) {
      return (
        model.dateOption === "seasonal" &&
        _.get(model, "seasonal.type", null) === "collect_month_day_between" &&
        ((_.get(model, "seasonal.between.start.month", null) &&
          _.get(model, "seasonal.between.start.date", null)) ||
          (_.get(model, "seasonal.between.end.month", null) &&
            _.get(model, "seasonal.between.end.date", null)))
      );
    },

    isValidCollectMonthDayOr(model) {
      return (
        model.dateOption === "seasonal" &&
        model.seasonal.type === "collect_month_day_or" &&
        _.get(model, "seasonal.or.lt.month", null) &&
        _.get(model, "seasonal.or.lt.date", null) &&
        _.get(model, "seasonal.or.gt.month", null) &&
        _.get(model, "seasonal.or.gt.date", null)
      );
    },

    isValidCollectDayBetween(model) {
      return (
        model.dateOption === "seasonal" &&
        model.seasonal.type === "collect_day" &&
        _.get(model, "seasonal.days_between.start", null) &&
        _.get(model, "seasonal.days_between.end", null)
      );
    },
  },
};
</script>
